import type { Card, GameState } from '@/types';

// 计算手牌强度（0-1）
export function calculateHandStrength(holeCards: [Card, Card], communityCards: Card[]): number {
  if (communityCards.length === 0) {
    return calculatePreflopStrength(holeCards);
  }
  
  // 使用蒙特卡洛模拟计算胜率
  return monteCarloEquity(holeCards, communityCards, 500);
}

// 翻牌前手牌强度
export function calculatePreflopStrength(holeCards: [Card, Card]): number {
  const [card1, card2] = holeCards;
  
  // 对子
  if (card1.value === card2.value) {
    const pairValue = card1.value;
    // AA = 1.0, 22 = 0.5
    return 0.5 + (pairValue - 2) / 24;
  }
  
  // 同花
  const isSuited = card1.suit === card2.suit;
  
  // 连牌
  const gap = Math.abs(card1.value - card2.value);
  const isConnected = gap === 1;
  
  // 基础分数
  let strength = (card1.value + card2.value) / 28; // 归一化到 0-1
  
  // 同花加成
  if (isSuited) strength += 0.1;
  
  // 连牌加成
  if (isConnected) strength += 0.08;
  else if (gap === 2) strength += 0.04;
  
  // AK 特殊处理
  if (card1.value >= 13 && card2.value >= 13) {
    strength += 0.15;
  }
  
  return Math.min(strength, 1);
}

// 蒙特卡洛模拟计算胜率
export function monteCarloEquity(
  holeCards: [Card, Card],
  communityCards: Card[],
  iterations: number = 1000
): number {
  const wins = { player: 0, opponent: 0, tie: 0 };
  
  for (let i = 0; i < iterations; i++) {
    const result = simulateShowdown(holeCards, communityCards);
    if (result > 0) wins.player++;
    else if (result < 0) wins.opponent++;
    else wins.tie++;
  }
  
  return (wins.player + wins.tie * 0.5) / iterations;
}

// 模拟一次摊牌
function simulateShowdown(playerCards: [Card, Card], communityCards: Card[]): number {
  // 创建剩余牌堆
  const usedCards = new Set([...playerCards, ...communityCards].map(c => c.id));
  const remainingDeck = createRemainingDeck(usedCards);
  
  // 随机洗牌
  const shuffled = shuffleArray(remainingDeck);
  
  // 给对手发牌
  const opponentCards: [Card, Card] = [shuffled[0], shuffled[1]];
  
  // 补全公共牌
  const neededCards = 5 - communityCards.length;
  const fullCommunity = [...communityCards, ...shuffled.slice(2, 2 + neededCards)];
  
  // 比较牌型
  const playerValue = evaluateSevenCards(playerCards, fullCommunity);
  const opponentValue = evaluateSevenCards(opponentCards, fullCommunity);
  
  if (playerValue > opponentValue) return 1;
  if (playerValue < opponentValue) return -1;
  return 0;
}

// 创建剩余牌堆
function createRemainingDeck(usedCards: Set<string>): Card[] {
  const suits = ['hearts', 'diamonds', 'clubs', 'spades'] as const;
  const ranks = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'] as const;
  
  const deck: Card[] = [];
  let id = 0;
  
  for (const suit of suits) {
    for (let i = 0; i < ranks.length; i++) {
      const cardId = `card-${id++}`;
      if (!usedCards.has(cardId)) {
        deck.push({
          id: cardId,
          suit,
          rank: ranks[i],
          value: i + 2,
        });
      }
    }
  }
  
  return deck;
}

// 洗牌
function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// 简化版七张牌评估（返回一个数值用于比较）
function evaluateSevenCards(holeCards: [Card, Card], communityCards: Card[]): number {
  const allCards = [...holeCards, ...communityCards];
  
  // 简化的评估：检查是否有对子、两对、三条等
  const rankCount = new Map<number, number>();
  const suitCount = new Map<string, number>();
  
  for (const card of allCards) {
    rankCount.set(card.value, (rankCount.get(card.value) || 0) + 1);
    suitCount.set(card.suit, (suitCount.get(card.suit) || 0) + 1);
  }
  
  let value = 0;
  
  // 检查四条
  for (const [rank, count] of rankCount) {
    if (count === 4) value += 10 ** 8 + rank * 100;
  }
  
  // 检查葫芦
  let hasThree = false;
  let threeRank = 0;
  for (const [rank, count] of rankCount) {
    if (count === 3) {
      hasThree = true;
      threeRank = rank;
      break;
    }
  }
  if (hasThree) {
    for (const [rank, count] of rankCount) {
      if (count >= 2 && rank !== threeRank) {
        value += 10 ** 7 + threeRank * 100 + rank;
        break;
      }
    }
  }
  
  // 检查同花
  for (const [suit, count] of suitCount) {
    if (count >= 5) {
      const suitedCards = allCards.filter(c => c.suit === suit).sort((a, b) => b.value - a.value);
      value += 10 ** 6 + suitedCards[0].value;
    }
  }
  
  // 检查顺子
  const uniqueRanks = [...new Set(allCards.map(c => c.value))].sort((a, b) => b - a);
  if (uniqueRanks.length >= 5) {
    for (let i = 0; i <= uniqueRanks.length - 5; i++) {
      if (uniqueRanks[i] - uniqueRanks[i + 4] === 4) {
        value += 10 ** 5 + uniqueRanks[i];
      }
    }
  }
  
  // 检查三条
  if (hasThree && value < 10 ** 7) {
    value += 10 ** 4 + threeRank * 100;
  }
  
  // 检查两对
  const pairs: number[] = [];
  for (const [rank, count] of rankCount) {
    if (count === 2) pairs.push(rank);
  }
  if (pairs.length >= 2) {
    pairs.sort((a, b) => b - a);
    value += 10 ** 3 + pairs[0] * 100 + pairs[1];
  } else if (pairs.length === 1) {
    value += 10 ** 2 + pairs[0] * 100;
  }
  
  // 高牌
  const sorted = allCards.sort((a, b) => b.value - a.value);
  value += sorted[0].value;
  
  return value;
}

// 计算底池赔率
export function calculatePotOdds(potSize: number, callAmount: number): number {
  if (callAmount === 0) return 1;
  return potSize / (potSize + callAmount);
}

// 计算位置优势（0-1，越晚位置越有利）
export function calculatePositionAdvantage(state: GameState, playerId: string): number {
  const playerIndex = state.players.findIndex(p => p.id === playerId);
  if (playerIndex === -1) return 0;
  
  const numPlayers = state.players.filter(p => p.status !== 'folded' && p.status !== 'eliminated').length;
  if (numPlayers <= 1) return 1;
  
  // 计算距离庄家的位置
  const distanceFromButton = (playerIndex - state.dealerIndex + state.players.length) % state.players.length;
  
  // 越接近庄家（距离越小），位置优势越大
  return 1 - (distanceFromButton / numPlayers);
}

// 估算对手手牌范围强度
export function estimateOpponentRange(state: GameState, opponentId: string): number {
  const opponent = state.players.find(p => p.id === opponentId);
  if (!opponent) return 0.5;
  
  // 基于对手的下注行为估算
  const aggression = opponent.totalBetThisRound / (state.blinds.big * 10);
  
  // 下注越大，手牌范围越强（简化模型）
  return Math.min(0.3 + aggression * 0.3, 0.9);
}
